import { request } from '@papan/api-client'
import type { TenantMode } from '@papan/types'

export interface TenantEmailInfo {
  email: string
  role: string
}

export interface TenantInfo {
  id: string
  name: string
  type: string
  /**
   * This tenant's EFFECTIVE mode (0175, §8), resolved server-side by the shared
   * rule. `'identity-only'` means the tenant is the person's identity but not a
   * workspace: no drive of their own, and no storage destination.
   *
   * Optional because an API instance that predates the field is reachable for
   * real — the deploy does a ROLLING restart, and this bundle can be served by
   * CF Pages before every instance is back. Read it through
   * `isIdentityOnlyTenant` below, never as `t.mode === 'identity-only'`
   * scattered across components, and never re-derived from `type` plus a guess
   * at the deployment default.
   */
  mode?: TenantMode
  emails: TenantEmailInfo[]
}

/**
 * Is this tenant identity-only? The SPA's ONE read of the field.
 *
 * ── Absent means FULL, and that is the safe direction here ──────────────
 *
 * This is a DISCLOSURE decision, not an enforcement one: every action it hides
 * is independently refused by the server (C2–C5), so the cost of guessing wrong
 * is a control that 403s rather than a control that should not have existed.
 * Guessing the other way is worse and more likely — an older API omits the
 * field, and treating that as identity-only would strip the drive out of the
 * product for every user on every deployment during a rolling restart.
 *
 * `undefined` is therefore full, deliberately, and this must not be "hardened"
 * into failing closed. UI hiding is not enforcement (§8); the server denial is.
 */
export function isIdentityOnlyTenant(tenant: Pick<TenantInfo, 'mode'> | null | undefined): boolean {
  return tenant?.mode === 'identity-only'
}

interface MemberInfo {
  email: string
  name: string
  role: string
  /**
   * Legacy field NAME preserved for wire back-compat. The value is
   * billing-micro of the deployment currency, NOT USD micro — the
   * "Usd" is historical. The server reads this from the JSONB
   * `memberships.budget_adjustments.month` since codex F6 (round 16),
   * which dropped the legacy scalar column in migration 0178.
   */
  budgetAdjustmentUsd: number | null
}

interface TeamInfo {
  id: string
  tenantId: string
  name: string
  parentId: string | null
  priority: number
  createdBy: string
  memberCount?: number
  createdAt: string
  updatedAt: string
}

interface TeamMemberInfo {
  id: string
  groupId: string
  email: string
  role: string  // 'member' | 'admin'
  addedBy: string
  createdAt: string
}

export const orgsApi = {
  async getCreateLimit() {
    return request<{ canCreate: boolean; freeOrgsOwned: number; maxFreeOrgs: number }>('/api/orgs/create-limit')
  },

  async createOrg(name: string) {
    return request<{ ok: boolean; tenantId: string; name: string }>('/api/orgs', {
      method: 'POST',
      body: JSON.stringify({ name }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async listMyTenants() {
    return request<{ tenants: TenantInfo[] }>('/api/orgs/my-tenants')
  },

  async updateName(tenantId: string, name: string) {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async deleteOrg(tenantId: string) {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}`, { method: 'DELETE' })
  },

  async listMembers(tenantId: string) {
    return request<{ members: MemberInfo[] }>(`/api/orgs/${tenantId}/members`)
  },

  async invite(tenantId: string, email: string, role?: string) {
    return request<{ ok: boolean; inviteId: string }>(`/api/orgs/${tenantId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async acceptInvite(token: string) {
    return request<{ ok: boolean; tenantId: string }>(`/api/orgs/invite/${token}/accept`, {
      method: 'POST',
    })
  },

  async changeRole(tenantId: string, email: string, role: string) {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}/members/role`, {
      method: 'PATCH',
      body: JSON.stringify({ email, role }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  /**
   * The value is billing-micro of the deployment currency; the
   * `budgetAdjustmentUsd` param NAME is a legacy alias kept for wire
   * back-compat. The route also accepts `capBillingMicro` for new
   * clients (see the PATCH `budget` route's body handling).
   */
  async setBudget(tenantId: string, email: string, budgetAdjustmentUsd: number | null) {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}/members/budget`, {
      method: 'PATCH',
      body: JSON.stringify({ email, budgetAdjustmentUsd }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async removeMember(tenantId: string, email: string) {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}/members/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    })
  },

  async listInvites(tenantId: string) {
    return request<{ invites: any[] }>(`/api/orgs/${tenantId}/invites`)
  },

  async listMyInvites() {
    return request<{ invites: Array<{ inviteId: string; token: string; tenantId: string; tenantName: string; email: string; role: string }> }>('/api/orgs/my-invites')
  },

  /** #1152: `code` is the one-time code sent to the invited address — needed when the account hasn't proven it
   *  (the accept then fails with `code: 'INVITE_ADDRESS_PROOF_REQUIRED'`). */
  async acceptInviteByToken(token: string, code?: string) {
    return request<{ ok: boolean; tenantId: string }>(`/api/orgs/invite/${token}/accept`, {
      method: 'POST',
      ...(code ? { body: JSON.stringify({ code }) } : {}),
    })
  },

  /** #1152: send the one-time code to the invited address (`alreadyProven` when none is needed). */
  async sendInviteCode(token: string) {
    return request<{ ok: boolean; email?: string; alreadyProven?: boolean; devCode?: string }>(`/api/orgs/invite/${token}/send-code`, {
      method: 'POST',
    })
  },

  async declineInvite(token: string) {
    return request<{ ok: boolean }>(`/api/orgs/invite/${token}/decline`, {
      method: 'POST',
    })
  },

  async blockInvite(token: string) {
    return request<{ ok: boolean }>(`/api/orgs/invite/${token}/block`, {
      method: 'POST',
    })
  },

  async listBlockedInvites() {
    return request<{ blocked: Array<{ inviteId: string; tenantId: string; tenantName: string; email: string; scope?: 'account' | 'address' }> }>('/api/orgs/my-blocked-invites')
  },

  async unblockInvite(inviteId: string) {
    return request<{ ok: boolean }>(`/api/orgs/invite/${inviteId}/unblock`, {
      method: 'POST',
    })
  },

  // ─── Teams ─────────────────────────────────────────────────────

  async listTeams(tenantId: string) {
    return request<{ teams: TeamInfo[] }>(`/api/orgs/${tenantId}/teams`)
  },

  async createTeam(tenantId: string, data: { name: string; parentId?: string; priority?: number }) {
    return request<{ ok: boolean; team: TeamInfo }>(`/api/orgs/${tenantId}/teams`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async updateTeam(tenantId: string, teamId: string, data: { name?: string; parentId?: string | null; priority?: number }) {
    return request<{ ok: boolean; team: TeamInfo }>(`/api/orgs/${tenantId}/teams/${teamId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async reorderTeams(tenantId: string, teamIds: string[]) {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}/teams/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ teamIds }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async deleteTeam(tenantId: string, teamId: string) {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}/teams/${teamId}`, {
      method: 'DELETE',
    })
  },

  async listTeamMembers(tenantId: string, teamId: string) {
    return request<{ members: TeamMemberInfo[] }>(`/api/orgs/${tenantId}/teams/${teamId}/members`)
  },

  async addTeamMember(tenantId: string, teamId: string, email: string) {
    return request<{ ok: boolean; member: TeamMemberInfo }>(`/api/orgs/${tenantId}/teams/${teamId}/members`, {
      method: 'POST',
      body: JSON.stringify({ email }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async removeTeamMember(tenantId: string, teamId: string, email: string) {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}/teams/${teamId}/members/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    })
  },

  async updateTeamMemberRole(tenantId: string, teamId: string, email: string, role: string) {
    return request<{ ok: boolean }>(`/api/orgs/${tenantId}/teams/${teamId}/members/${encodeURIComponent(email)}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
      headers: { 'Content-Type': 'application/json' },
    })
  },

  async getMyTeams(tenantId: string) {
    return request<{ teams: Array<{ id: string; priority: number }> }>(`/api/orgs/${tenantId}/teams/my`)
  },
}

export * from './budget.js'

/** Re-exported so a consumer outside this feature (the shell's
 *  notification actions) can reach it through the package's one client
 *  entry point, like everything else here. */
export { storageApi } from './storage'
